import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import SessionPermissionsLoadState from "permissions/session/_views/session-permissions-load-state";
import { useCallback, useEffect, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";

/**
 * Fetches the session permissions if they are not already in hand, and returns what to render while
 * the page does not have them.
 *
 * Pages gate their content on the session permissions, so each one needs the same three things: the
 * request, a retry when it fails, and something to show meanwhile. Those were written out per page,
 * which is how eight of them ended up rendering a loader with no error branch at all — see
 * {@link SessionPermissionsLoadState} for what that cost. Owning all three here means a page cannot
 * accidentally omit the failure case, because it no longer writes the success case either.
 *
 * Call it unconditionally, as a hook, and render the result only when the permissions are absent:
 *
 * ```tsx
 * const permissionsLoadState = useSessionPermissionsLoadState();
 * if (!sessionPermissions) return permissionsLoadState;
 * if (!sessionPermissions.includesAccessToThing) return <NotAuthorized />;
 * ```
 */
export default function useSessionPermissionsLoadState(): ReactNode {
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const loading = useSelector((state: RootState) => state.permissions.session.loading);
  const error = useSelector((state: RootState) => state.permissions.session.error);

  useEffect(() => {
    if (!sessionPermissions && !error && !loading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, error, loading]);

  const retry = useCallback(() => {
    if (!loading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, loading]);

  return <SessionPermissionsLoadState error={error} onRetry={retry} />;
}
